import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Dashboard.css"; 

// ✅ HELPER: Live Countdown Component (Fixed Logic)
function WarrantyCountdown({ startDate, durationStr, label }) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!startDate || !durationStr) return;

    // 1. Extract number of years (e.g. "25 Years" -> 25)
    const yearsMatch = durationStr.match(/(\d+)/);
    const durationYears = yearsMatch ? parseInt(yearsMatch[0], 10) : 0;

    if (durationYears === 0) return;

    // 2. Calculate Expiry Date
    const start = new Date(startDate);
    const expiry = new Date(start);
    expiry.setFullYear(start.getFullYear() + durationYears);

    // 3. Update Timer Function (Precise Calendar Calculation)
    const updateTimer = () => {
      const now = new Date();
      const difference = expiry - now;

      if (difference > 0) {
        // Calculate Calendar Years first
        let years = expiry.getFullYear() - now.getFullYear();
        let monthDiff = expiry.getMonth() - now.getMonth();
        let dayDiff = expiry.getDate() - now.getDate();

        // If the current date hasn't reached the expiry month/day yet, subtract a year
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
          years--;
        }

        // Calculate remaining days/hours from the last full year anniversary
        const lastAnniversary = new Date(now);
        lastAnniversary.setFullYear(now.getFullYear() + years);
        
        // SIMPLIFIED ACCURATE LOGIC:
        // 1. Create a date object representing "Now + Years"
        const tempDate = new Date(now);
        tempDate.setFullYear(now.getFullYear() + years);
        
        const diffFromAnniversary = expiry - tempDate;
        
        const days = Math.floor(diffFromAnniversary / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffFromAnniversary % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        setTimeLeft(`${years} Yr ${days} Days ${hours} Hrs Remaining`);
      } else {
        setTimeLeft("Expired");
      }
    };

    updateTimer(); // Run immediately
    const timerId = setInterval(updateTimer, 60000); // Update every minute

    return () => clearInterval(timerId);
  }, [startDate, durationStr]);

  if (!timeLeft) return null;

  return (
    <div style={{ marginBottom: "12px", background: "white", padding: "8px", borderRadius: "6px", border: "1px solid #eee" }}>
      <strong style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "4px" }}>{label}</strong>
      <span style={{ fontSize: "13px", fontWeight: "700", color: "#2e7d32" }}>⏳ {timeLeft}</span>
    </div>
  );
}

export default function Dashboard({ user, onLogout }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // Workflow steps with Icons
  const workflowSteps = [
    { key: "leadSurvey", label: "Lead & Survey", icon: "📋" },
    { key: "systemDesign", label: "System Design", icon: "📐" },
    { key: "approval", label: "Govt. Approvals", icon: "🏛️" },
    { key: "procurement", label: "Material Procurement", icon: "📦" },
    { key: "installation", label: "System Installation", icon: "🛠️" },
    { key: "netMeter", label: "Net Metering", icon: "⚡" },
    { key: "handover", label: "Project Handover", icon: "🤝" },
  ];

  // Document List for the Sidebar
  const documentList = [
    { key: "quotation", label: "📄 Feasibility Letter" },
    { key: "agreement", label: "📝 Net Metering Agreement" },
    { key: "layout", label: "📐 Commissioning Certificate" },
    { key: "netMeter", label: "⚡ Project Completion Certificate" },
    { key: "subsidy", label: "📄 Operation & Maintenance Guideline" },
    { key: "commissioning", label: "✅ Panel & Inverter Data" },
  ];

  /* ================= FETCH CLIENT PROJECT ================= */
  useEffect(() => {
    if (!user?.systemId) return;

    axios
      .get(`https://meetra-green-energy-backend.onrender.com/api/projects/client/${user.systemId}`)
      .then((res) => {
        setProject(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load project", err);
        setLoading(false);
      });
  }, [user]);

  // Helper for Initials
  const getInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'ME';
  };

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric"
    });
  };

  return (
    <div className="dashboard-container">
      
      {/* ================= HEADER ================= */}
      <header className="navbar">
        <div className="brand">
          <div className="brand-logo">MG</div>
          <div>
            <h1>Meetra Green Energy</h1>
            <span>Powering a Sustainable Future</span>
          </div>
        </div>

        <div className="header-actions">
          <div className="user-profile">
            <div className="avatar">{getInitials(user?.fullName)}</div>
            <div className="user-info">
              <span className="customer-name">{user?.fullName || "Valued Customer"}</span>
              <span className="customer-id">ID: {user?.systemId}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={onLogout}>
            <span className="icon">↪</span> Logout
          </button>
        </div>
      </header>

      {/* ================= MAIN CONTENT ================= */}
      <main className="dashboard-content">
        
        {/* ================= WELCOME HERO ================= */}
        <section className="welcome-hero">
          <div className="hero-text">
            <h2>Welcome Back, {user?.fullName?.split(" ")[0] || "Friend"}! 👋</h2>
            <p>Here is the real-time status of your solar installation project.</p>
          </div>
          <div className="hero-stats">
             <div className="stat-pill">🌱 <span>12 Tons CO2 Saved (Est.)</span></div>
             <div className="stat-pill">☀️ <span>Green Energy Transition</span></div>
          </div>
        </section>

        {/* ================= PROJECT INFO CARDS ================= */}
        <section className="project-summary">
          {project ? (
            <div className="project-info-cards">
              <div className="info-card location-card">
                <div className="card-icon">📍</div>
                <div>
                  <span className="label">Site Location</span>
                  <strong>{project.siteLocation}</strong>
                </div>
              </div>
              <div className="info-card system-card">
                <div className="card-icon">⚡</div>
                <div>
                  <span className="label">System Capacity</span>
                  <strong>{project.systemSize}</strong>
                </div>
              </div>
              <div className="info-card ref-card">
                <div className="card-icon">🆔</div>
                <div>
                  <span className="label">Reference ID</span>
                  <strong>{project.clientRef}</strong>
                </div>
              </div>
            </div>
          ) : (
             !loading && <div className="no-project-msg"><p>No active project found linked to this account.</p></div>
          )}
        </section>

        {/* ================= MAIN GRID ================= */}
        <section className="dashboard-grid">
          
          {/* ================= LIVE TIMELINE ================= */}
          <section className="project-status">
             <div className="section-header">
                <div>
                  <h3>🚀 Live Project Tracker</h3>
                  <p>Track every step of your installation journey.</p>
                </div>
                <div className="live-indicator">
                  <span className="dot"></span> Live Updates
                </div>
             </div>

             {loading ? (
                <div className="loading-state">Loading timeline...</div>
             ) : project ? (
               <div className="timeline-wrapper">
                  <div className="vertical-line"></div>
                  <ul className="status-timeline">
                    {workflowSteps.map((step, index) => {
                      const status = project.progressFlow?.[step.key] || "pending";
                      
                      return (
                        <li key={step.key} className={`timeline-step ${status}`} style={{animationDelay: `${index * 0.1}s`}}>
                          
                          {/* Status Icon Bubble */}
                          <div className="step-icon-wrapper">
                             <div className="step-icon">
                                {step.icon}
                             </div>
                          </div>

                          {/* Content */}
                          <div className="step-content">
                             <div className="step-header">
                               <div className="label-group">
                                 <span className="step-label">{step.label}</span>
                                 {status === "completed" && <span className="check-badge">✔</span>}
                               </div>

                               {status === "completed" && <span className="status-text success">Completed</span>}
                               {status === "in-progress" && <span className="status-text warning">In Progress...</span>}
                               {status === "pending" && <span className="status-text neutral">Upcoming</span>}
                             </div>
                             
                             {/* Progress Bar */}
                             {status === "in-progress" && (
                               <div className="progress-bar-container">
                                 <div className="progress-bar-fill"></div>
                               </div>
                             )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
               </div>
             ) : (
               <p>No timeline data available.</p>
             )}
          </section>

          {/* ================= SIDEBAR ================= */}
          <aside className="dashboard-aside">
            
            {/* ✅ LIVE WARRANTY WIDGET */}
            {project?.warrantyDetails?.startDate && (
              <section className="widget-card warranty-widget" style={{background: "linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)", border: "1px solid #bbdefb"}}>
                <div className="widget-header">
                   <h3 style={{color: "#0d47a1"}}>🛡️ Warranty Status</h3>
                   <span className="badge success">Active</span>
                </div>
                
                <div className="warranty-details" style={{fontSize: "13px", color: "#444"}}>
                  <div style={{marginBottom: "15px", borderBottom: "1px solid #ddd", paddingBottom: "10px"}}>
                    <strong>Start Date:</strong> {formatDate(project.warrantyDetails.startDate)}
                  </div>

                  {/* Panel Warranty Countdown */}
                  <WarrantyCountdown 
                    startDate={project.warrantyDetails.startDate} 
                    durationStr={project.warrantyDetails.panelWarranty} 
                    label="☀️ Solar Panels Coverage"
                  />

                  {/* Inverter Warranty Countdown */}
                  <WarrantyCountdown 
                    startDate={project.warrantyDetails.startDate} 
                    durationStr={project.warrantyDetails.inverterWarranty} 
                    label={`⚡ Inverter (${project.warrantyDetails.inverterName})`}
                  />

                </div>
              </section>
            )}

            {/* DOCUMENTS LIST */}
            <section className="widget-card documents-widget">
              <div className="widget-header">
                 <h3>📂 All Documents</h3>
                 <span className="badge">Updated</span>
              </div>
              <ul className="doc-list">
                 {documentList.map((doc) => {
                   const fileUrl = project?.documents?.[doc.key];
                   return (
                     <li key={doc.key}>
                       <span>{doc.label}</span>
                       {fileUrl ? (
                         <a href={`https://meetra-green-energy-backend.onrender.com${fileUrl}`} target="_blank" rel="noreferrer" style={{color: "var(--primary)", fontWeight: "600", textDecoration: "underline"}}>
                           Download
                         </a>
                       ) : (
                         <span style={{color: "#aaa", fontSize: "11px"}}>Pending</span>
                       )}
                     </li>
                   );
                 })}
              </ul>
            </section>

            {/* Support Widget */}
            <section className="widget-card contact-widget">
              <h3>📞 Dedicated Support</h3>
              <p>Your Project Manager:</p>
              <div className="manager-info">
                 <div className="manager-avatar">RK</div>
                 <div>
                   <strong>Rajnikant Hirpara</strong>
                   <span>Project Head</span>
                 </div>
              </div>
              <a href="tel:+918200197199" className="contact-link phone">📞 +91 82001 97199</a>
              <a href="mailto:meetragreen@gmail.com" className="contact-link email">✉️ meetragreen@gmail.com</a>
            </section>

          </aside>
        </section>
      </main>
    </div>
  );
}